// src/tabs/logements/LivretConfigEditor.jsx — Phase A.4 (ADR-114)
// + PR-FIX-4d (ADR-162) — carte interactive Leaflet pour poser le marqueur
//   logement (weather_lat/weather_lng) par drag-drop visuel au lieu de saisie
//   texte. Source de vérité reste les inputs lat/lng (lecture seule par défaut,
//   passage en édition manuelle via bouton).
// + ADR-176 — Geocoding auto adresse logement.
//   Bouton "🔍 Géocoder" appelle /api/beds24-mapping?action=admin_geocode_address.
//   Pré-place le marqueur Leaflet aux coords retournées par Google.
//   Correctifs pessimist embarqués : B-1 (drag prime fetch), B-2 (AbortController
//   useRef), B-3 (fermeture modal re-géocoder dans finally), M-1 (note
//   permanente save Infos), M-2 (bandeau + marqueur opacity), M-4 (catch
//   GOOGLE_PLACES_KEY côté serveur), M-5 (texte aide inline), m-1 (wording
//   bouton).
//
// Section config livret : titre FR/EN, welcome subtitle FR/EN.
// ADR-199 — contact_email/contact_phone RETIRÉS (source unique cfg.co).
// Champs hors-scope A.4 (déférés A.5) :
//   - hours (JSONB structuré)
//   - wifi_networks, social_networks
//   - google_maps_url, weather_lat/lng, etc.
//
// Q-D5=A : bouton "Enregistrer config" explicite + dirty state local.

import { useState, useEffect, useRef, useCallback, Suspense, lazy } from "react";
import { DS } from "../../lib/ui";
import { Settings, MapPin, Ticket, Save, Map as MapIcon, ExternalLink, AlertTriangle, CheckCircle2, Search } from "lucide-react";
import { supabase } from "../../supabase";
import { Inp, Textarea, lblStyle, SectionCard } from "./atoms";
import { autoTranslateEmptyLangs } from "../../lib/livret-autotranslate";

// Pastille de teinte-catégorie portant l'icône lucide (DA §2/§8 ter).
function cfgCatPill(Ico, bg, ico) {
  return (
    <span style={{
      display: "inline-flex", width: 26, height: 26, borderRadius: 7,
      background: bg, color: ico, alignItems: "center", justifyContent: "center",
      verticalAlign: "middle", marginRight: 2,
    }}>
      <Ico size={15} strokeWidth={1.8} />
    </span>
  );
}

// L1a — cartes repliables config livret (esprit Instructions). Clé session
// DISTINCTE de l'onglet Instructions (fbz-livret-cards-open) pour ne pas
// collisionner. Défaut TOUT FERMÉ.
const LIVRET_CFG_CARDS_KEY = "fbz-livret-config-cards-open";
const LIVRET_CFG_CARD_IDS = ["config", "loc", "gyg"];
function readLivretCfgCards() {
  const allClosed = () => Object.fromEntries(LIVRET_CFG_CARD_IDS.map((id) => [id, false]));
  try {
    const raw = sessionStorage.getItem(LIVRET_CFG_CARDS_KEY);
    if (!raw) return allClosed();
    const parsed = JSON.parse(raw);
    const base = allClosed();
    for (const id of LIVRET_CFG_CARD_IDS) if (typeof parsed?.[id] === "boolean") base[id] = parsed[id];
    return base;
  } catch (_) {
    return allClosed();
  }
}
const LIVRET_CFG_CARD_META = {
  config: { icon: cfgCatPill(Settings, DS.catArdoiseBg, DS.catArdoiseIco), title: "Configuration", accent: DS.catArdoiseIco, titleColor: DS.marineDark, headerBg: DS.catArdoiseBg, bg: DS.card },
  loc: { icon: cfgCatPill(MapPin, DS.catCielBg, DS.catCielIco), title: "Localisation & météo", accent: DS.catCielIco, titleColor: DS.marineDark, headerBg: DS.catCielBg, bg: DS.card },
  gyg: { icon: cfgCatPill(Ticket, DS.catCorailBg, DS.catCorailIco), title: "GetYourGuide", accent: DS.catCorailIco, titleColor: DS.marineDark, headerBg: DS.catCorailBg, bg: DS.card },
}

// L6 — Config n'affiche QUE la langue active (pilotée par le sélecteur global).
const CFG_LANG_UP = { fr: "FR", en: "EN", es: "ES" }
const CFG_TITLE_PH = {
  fr: "ex : Bienvenue à Cocon FBZ",
  en: "ex : Welcome to Cocon FBZ",
  es: "ex : Bienvenido a Cocon FBZ",
}
const CFG_WELCOME_PH = {
  fr: "Phrase d'accroche pour le voyageur",
  en: "Welcome tagline",
  es: "Frase de bienvenida",
}

// PR-FIX-4d (ADR-162) — lazy-load Leaflet ~62 kB chunk admin (pattern cohérent
// AlentoursMap voyageur ADR-153/155).
const AlentoursMarkerDragMap = lazy(() =>
  import("./AlentoursMarkerDragMap.jsx"),
);

// ADR-176 — Messages utilisateur par reason Google Geocoding (FR uniquement,
// composant admin pas voyageur).
const GEO_REASON_MESSAGES = {
  ZERO_RESULTS:
    "Adresse introuvable par Google. Vérifiez l'orthographe ou la ville.",
  OVER_QUERY_LIMIT:
    "Quota Google atteint. Réessayez dans quelques minutes.",
  REQUEST_DENIED:
    "API Geocoding désactivée. Contactez un développeur.",
  NETWORK_ERROR:
    "Timeout Google (>8s). Vérifiez la connexion ou réessayez.",
  INVALID_REQUEST: "Adresse trop courte ou invalide.",
  UNKNOWN:
    "Erreur inattendue. Réessayez ou saisissez les coordonnées manuellement.",
};

const GEO_ERROR_MESSAGES = {
  address_required: "Adresse manquante. Renseignez-la dans l'onglet Infos.",
  address_too_short: "Adresse trop courte (minimum 5 caractères).",
  address_too_long: "Adresse trop longue (maximum 300 caractères).",
};

async function getAuthHeaders() {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  const token = session?.access_token;
  return token
    ? { "Content-Type": "application/json", Authorization: `Bearer ${token}` }
    : { "Content-Type": "application/json" };
}

export default function LivretConfigEditor({
  config,
  logement_id,
  onSave,
  lang = "fr",
}) {
  const initialTitleFr = config?.title?.fr || "";
  const initialTitleEn = config?.title?.en || "";
  const initialTitleEs = config?.title?.es || "";
  const initialWelcomeFr = config?.welcome_subtitle?.fr || "";
  const initialWelcomeEn = config?.welcome_subtitle?.en || "";
  const initialWelcomeEs = config?.welcome_subtitle?.es || "";
  // ADR-199 — contact (email/tel) RETIRÉ d'ici : source unique = cfg.co
  // (Paramètres → ⚙️ Général), dérivé read-time côté livret. Ne plus saisir ici.
  // Phase A.5 (ADR-117) — config météo
  const initialWeatherCity = config?.weather_city || "";
  const initialWeatherLat =
    config?.weather_lat != null ? String(config.weather_lat) : "";
  const initialWeatherLng =
    config?.weather_lng != null ? String(config.weather_lng) : "";
  // PR-A93-2b/1 (ADR-180) — GYG MVP livret voyageur
  const initialGygEnabled = config?.getyourguide_enabled === true;
  const initialGygLocationId = config?.gyg_location_id || "";
  // BUG 18 — slug éditable (rétrocompat QR gérée serveur via old_slugs[]).
  const initialSlug = config?.slug || "";
  // BUG 21 — nombre d'activités GYG par logement (vide = défaut voyageur 5).
  const initialGygNumberOfItems = config?.gyg_number_of_items || "";

  const [slug, setSlug] = useState(initialSlug);
  const [titleFr, setTitleFr] = useState(initialTitleFr);
  const [titleEn, setTitleEn] = useState(initialTitleEn);
  const [titleEs, setTitleEs] = useState(initialTitleEs);
  const [welcomeFr, setWelcomeFr] = useState(initialWelcomeFr);
  const [welcomeEn, setWelcomeEn] = useState(initialWelcomeEn);
  const [welcomeEs, setWelcomeEs] = useState(initialWelcomeEs);
  // L1a — état cartes repliables.
  const [openCards, setOpenCards] = useState(readLivretCfgCards);
  useEffect(() => {
    try {
      sessionStorage.setItem(LIVRET_CFG_CARDS_KEY, JSON.stringify(openCards));
    } catch (_) {}
  }, [openCards]);
  const toggleCard = useCallback((id) => {
    setOpenCards((p) => ({ ...p, [id]: p[id] === false }));
  }, []);
  const [weatherCity, setWeatherCity] = useState(initialWeatherCity);
  const [weatherLat, setWeatherLat] = useState(initialWeatherLat);
  const [weatherLng, setWeatherLng] = useState(initialWeatherLng);
  const [gygEnabled, setGygEnabled] = useState(initialGygEnabled);
  const [gygLocationId, setGygLocationId] = useState(initialGygLocationId);
  const [gygNumberOfItems, setGygNumberOfItems] = useState(
    initialGygNumberOfItems,
  );
  // PR-FIX-4d (ADR-162) — mode édition manuelle (saisie texte fallback).
  // Par défaut : false → la carte est la source de vérité interactive,
  // les inputs lat/lng sont lecture seule.
  const [manualLatLngEdit, setManualLatLngEdit] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  // ADR-176 — états geocoding séparés du flux save (zones d'affichage
  // distinctes : geocodeError affiché dans la section coordonnées, error
  // dans la section save).
  const [geocoding, setGeocoding] = useState(false);
  const [geocodeError, setGeocodeError] = useState(null);
  // ⚠️ Zone INFO POSITIVE séparée (B-1 drag prime fetch) — fond bleu doux,
  // pas rouge erreur. Auto-hide 3s.
  const [geocodeInfo, setGeocodeInfo] = useState(null);
  const [pendingGeoResult, setPendingGeoResult] = useState(null);
  const [showRegeocodeConfirm, setShowRegeocodeConfirm] = useState(false);

  // B-2 : AbortController via useRef (pas useState, pas de re-render parasite).
  const geocodeAbortRef = useRef(null);
  // B-1 : flag drag manuel pendant fetch in-flight (sentinelle mutation pure).
  const draggedDuringFetchRef = useRef(false);
  // Auto-hide info timer (B-1 message positif).
  const infoTimerRef = useRef(null);

  // Re-sync local si prop config change (post-save parent re-fetch)
  useEffect(() => {
    setSlug(config?.slug || "");
    setTitleFr(config?.title?.fr || "");
    setTitleEn(config?.title?.en || "");
    setTitleEs(config?.title?.es || "");
    setWelcomeFr(config?.welcome_subtitle?.fr || "");
    setWelcomeEn(config?.welcome_subtitle?.en || "");
    setWelcomeEs(config?.welcome_subtitle?.es || "");
    setWeatherCity(config?.weather_city || "");
    setWeatherLat(config?.weather_lat != null ? String(config.weather_lat) : "");
    setWeatherLng(config?.weather_lng != null ? String(config.weather_lng) : "");
    setGygEnabled(config?.getyourguide_enabled === true);
    setGygLocationId(config?.gyg_location_id || "");
    setGygNumberOfItems(config?.gyg_number_of_items || "");
    setError(null);
  }, [config]);

  // Cleanup timers à l'unmount.
  useEffect(() => {
    return () => {
      if (infoTimerRef.current) {
        clearTimeout(infoTimerRef.current);
      }
      // Abort fetch geocoder si en cours à l'unmount.
      if (geocodeAbortRef.current) {
        geocodeAbortRef.current.abort();
      }
    };
  }, []);

  // Q-3 — mountedRef pour éviter setState post-unmount (React 18 silencieux
  // mais on garde la trace pour la propreté).
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Q-1 a11y — Esc ferme la modal ouverte (pendingGeoResult OU
  // showRegeocodeConfirm). Ignoré pendant fetch in-flight côté re-géocoder
  // (bouton Annuler déjà disabled, cohérence UX).
  useEffect(() => {
    if (!pendingGeoResult && !showRegeocodeConfirm) return undefined;
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      if (pendingGeoResult) {
        setPendingGeoResult(null);
      } else if (showRegeocodeConfirm && !geocoding) {
        setShowRegeocodeConfirm(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [pendingGeoResult, showRegeocodeConfirm, geocoding]);

  const dirty =
    slug !== initialSlug ||
    titleFr !== initialTitleFr ||
    titleEn !== initialTitleEn ||
    titleEs !== initialTitleEs ||
    welcomeFr !== initialWelcomeFr ||
    welcomeEn !== initialWelcomeEn ||
    welcomeEs !== initialWelcomeEs ||
    weatherCity !== initialWeatherCity ||
    weatherLat !== initialWeatherLat ||
    weatherLng !== initialWeatherLng ||
    gygEnabled !== initialGygEnabled ||
    gygLocationId !== initialGygLocationId ||
    gygNumberOfItems !== initialGygNumberOfItems;

  // PR-A93-2b/1 (ADR-180) — validation client GYG : si toggle activé, ID requis
  // (mirror serveur regex /^\d{1,10}$/ via validateGygLocationId).
  const gygIdTrimmed = gygLocationId.trim();
  const gygIdValid = gygIdTrimmed === "" || /^\d{1,10}$/.test(gygIdTrimmed);
  const gygActivationBlocked = gygEnabled && gygIdTrimmed === "";

  // BUG 18 — validation slug client (miroir serveur SLUG_REGEX /^[a-z0-9-]+$/,
  // longueur 3..60). Le champ slug n'est affiché que pour une config existante.
  const slugTrimmed = slug.trim();
  const slugValid =
    /^[a-z0-9-]+$/.test(slugTrimmed) &&
    slugTrimmed.length >= 3 &&
    slugTrimmed.length <= 60;
  const slugChanged = slugTrimmed !== initialSlug;
  // BUG 21 — nombre d'activités GYG : vide OK (défaut voyageur 5), sinon
  // 1..2 chiffres dans la plage [1, 50] (mirror serveur validateGygNumberOfItems).
  const gygItemsTrimmed = gygNumberOfItems.trim();
  const gygItemsValid =
    gygItemsTrimmed === "" ||
    (/^\d{1,2}$/.test(gygItemsTrimmed) &&
      Number(gygItemsTrimmed) >= 1 &&
      Number(gygItemsTrimmed) <= 50);

  // Titre FR requis pour pouvoir sauvegarder + GYG cohérent (toggle ON → ID rempli + valide).
  // BUG 18 : si le slug a changé, il doit être valide.
  const canSave =
    dirty &&
    titleFr.trim().length > 0 &&
    !saving &&
    gygIdValid &&
    gygItemsValid &&
    !gygActivationBlocked &&
    (!slugChanged || slugValid);

  // ADR-176 — adresse source pour géocodage (lecture prop config, pas state).
  const addressForGeocode =
    typeof config?.address_full === "string" ? config.address_full.trim() : "";
  const addressEmpty = addressForGeocode.length < 5;
  const geocodeBtnDisabled = geocoding || addressEmpty;

  // M-2 : bandeau "Aucune coordonnée" si lat ET lng vides.
  const noCoordsYet =
    !weatherLat.trim() && !weatherLng.trim();

  // m-1 : wording bouton selon présence coords.
  const geocodeBtnLabel = noCoordsYet
    ? "Géocoder cette adresse"
    : "Géocoder depuis l'adresse";

  // PR-FIX-4d (ADR-162) — handler drag marqueur carte : met à jour weather_lat
  // + weather_lng en local. Le commit Supabase reste sur clic "Enregistrer".
  // Arrondit à 6 décimales (precision ~11 cm, largement suffisant pour météo
  // et géocodage logement, évite floats sale comme 43.604500000000004).
  // ADR-176 B-1 : si un fetch geocoder est in-flight, on note le drag manuel
  // dans le ref → applyGeocoderResult sera ignoré au retour fetch.
  function handleMarkerDragEnd(lat, lng) {
    if (typeof lat !== "number" || typeof lng !== "number") return;
    if (Number.isNaN(lat) || Number.isNaN(lng)) return;
    // B-1 : si fetch in-flight → flag drag manuel.
    if (geocoding) {
      draggedDuringFetchRef.current = true;
    }
    const round6 = (n) => Math.round(n * 1e6) / 1e6;
    setWeatherLat(String(round6(lat)));
    setWeatherLng(String(round6(lng)));
  }

  // ADR-176 — applique le résultat geocoder (post-confirm partial_match ou
  // succès direct). Arrondit à 6 décimales (cohérent handleMarkerDragEnd).
  function applyGeocoderResult(data) {
    const round6 = (n) => Math.round(n * 1e6) / 1e6;
    setWeatherLat(String(round6(data.lat)));
    setWeatherLng(String(round6(data.lng)));
    setPendingGeoResult(null);
    setGeocodeError(null);
    showInfoToast("Position mise à jour — enregistrez pour confirmer.");
  }

  // ADR-176 — helper toast info positive (zone bleue séparée, 3s auto-hide).
  function showInfoToast(msg) {
    setGeocodeInfo(msg);
    if (infoTimerRef.current) clearTimeout(infoTimerRef.current);
    infoTimerRef.current = setTimeout(() => {
      setGeocodeInfo(null);
      infoTimerRef.current = null;
    }, 3000);
  }

  // ADR-176 — handler principal géocodage.
  async function handleGeocode(opts = {}) {
    const { skipRegeocodeConfirm = false } = opts;

    // Guard adresse absente (defense-in-depth client + serveur).
    if (addressEmpty) {
      setGeocodeError(
        "Renseignez d'abord l'adresse dans l'onglet Infos.",
      );
      return;
    }

    // Guard 1 : si coords déjà présentes (au moins une) → modal "Re-géocoder".
    const hasCoords =
      weatherLat.trim() !== "" || weatherLng.trim() !== "";
    if (hasCoords && !skipRegeocodeConfirm) {
      setShowRegeocodeConfirm(true);
      return;
    }

    // B-1 : reset flag drag-pendant-fetch au début de chaque géocodage.
    draggedDuringFetchRef.current = false;

    // B-2 : abort fetch précédent si stale (ref, pas setState).
    if (geocodeAbortRef.current) {
      geocodeAbortRef.current.abort();
    }
    geocodeAbortRef.current = new AbortController();
    const signal = geocodeAbortRef.current.signal;

    setGeocoding(true);
    setGeocodeError(null);
    setGeocodeInfo(null);
    setPendingGeoResult(null);

    try {
      const headers = await getAuthHeaders();
      const res = await fetch(
        "/api/beds24-mapping?action=admin_geocode_address",
        {
          method: "POST",
          headers,
          body: JSON.stringify({ address: addressForGeocode }),
          signal,
        },
      );

      if (signal.aborted) return; // abort en cours, silencieux

      let data;
      try {
        data = await res.json();
      } catch (_e) {
        setGeocodeError("Réponse serveur invalide. Réessayez.");
        return;
      }

      if (res.status === 429) {
        setGeocodeError(
          `Trop d'appels — réessayez dans ${data.retryAfter || 60}s.`,
        );
        return;
      }
      if (res.status === 401) {
        // Q-4 — session expirée message dédié (vs "Erreur serveur" générique).
        setGeocodeError("Session expirée. Reconnectez-vous puis réessayez.");
        return;
      }
      if (!res.ok) {
        setGeocodeError(
          GEO_ERROR_MESSAGES[data && data.error] ||
            "Erreur serveur inattendue.",
        );
        return;
      }
      if (!data || !data.ok) {
        setGeocodeError(
          GEO_REASON_MESSAGES[data && data.reason] ||
            "Adresse introuvable.",
        );
        return;
      }

      // B-1 : si l'admin a dragué pendant ce fetch → abandonner le résultat.
      if (draggedDuringFetchRef.current) {
        showInfoToast(
          "Placement manuel conservé — géocodage ignoré.",
        );
        return;
      }

      // partial_match → modal confirmation
      if (data.partial_match) {
        setPendingGeoResult(data);
        return; // modal s'affiche (coords inchangées tant que pas confirmé)
      }

      // Succès strict → application directe
      applyGeocoderResult(data);
    } catch (e) {
      if (e && e.name === "AbortError") return; // abort silencieux
      setGeocodeError("Erreur réseau. Vérifiez votre connexion.");
    } finally {
      if (!signal.aborted) {
        setGeocoding(false);
      }
      // B-3 : fermeture modal Re-géocoder TOUJOURS après réponse fetch
      // (pas avant). La modal reste visible avec spinner pendant l'in-flight.
      setShowRegeocodeConfirm(false);
    }
  }

  async function handleSave() {
    if (!canSave) return;
    setSaving(true);
    setError(null);
    setSuccess(false);

    // L6 — auto-traduction AU SAVE : remplit EN/ES VIDES depuis le FR (jamais
    // d'écrasement d'une cible non vide, jamais bloquant sur fallback).
    const [trTitle, trWelcome] = await Promise.all([
      autoTranslateEmptyLangs({
        fr: titleFr,
        en: titleEn,
        es: titleEs,
        context: "title",
      }),
      autoTranslateEmptyLangs({
        fr: welcomeFr,
        en: welcomeEn,
        es: welcomeEs,
        context: "content",
      }),
    ]);
    // Refléter dans l'UI (la langue active visible montre la traduction au reload
    // de toute façon ; on synchronise les states pour cohérence immédiate).
    if (trTitle.en !== titleEn.trim()) setTitleEn(trTitle.en);
    if (trTitle.es !== titleEs.trim()) setTitleEs(trTitle.es);
    if (trWelcome.en !== welcomeEn.trim()) setWelcomeEn(trWelcome.en);
    if (trWelcome.es !== welcomeEs.trim()) setWelcomeEs(trWelcome.es);

    const title = { fr: titleFr.trim() };
    if (trTitle.en) title.en = trTitle.en;
    if (trTitle.es) title.es = trTitle.es;

    // Bug fix : welcome_subtitle JSONB validé non-vide côté serveur
    // (validateNonEmptyJsonbObject). Si aucune langue remplie, on doit OMETTRE
    // la clé du patch — sinon le backend retourne 400 "JSONB vide non autorisé".
    // Pattern : inclusion conditionnelle (cf. title qui est toujours rempli car
    // titleFr requis via canSave).
    const welcome_subtitle = {};
    if (welcomeFr.trim()) welcome_subtitle.fr = welcomeFr.trim();
    if (trWelcome.en) welcome_subtitle.en = trWelcome.en;
    if (trWelcome.es) welcome_subtitle.es = trWelcome.es;

    const patch = {
      logement_id,
      title,
      // BUG 18 — n'envoyer slug que s'il a changé (sinon old_slugs churn inutile).
      ...(slugChanged && { slug: slugTrimmed }),
      ...(Object.keys(welcome_subtitle).length > 0 && { welcome_subtitle }),
      // ADR-199 — contact_email/contact_phone ne sont plus écrits ici (source
      // unique cfg.co). Patch partiel : champs omis = inchangés en base.
      weather_city: weatherCity.trim() || null,
      weather_lat: weatherLat.trim() ? Number(weatherLat) : null,
      weather_lng: weatherLng.trim() ? Number(weatherLng) : null,
      // PR-A93-2b/1 (ADR-180) — GYG MVP livret voyageur
      getyourguide_enabled: gygEnabled,
      gyg_location_id: gygIdTrimmed || null,
      // BUG 21 — nombre d'activités GYG (null = défaut voyageur 5).
      gyg_number_of_items: gygItemsTrimmed || null,
    };

    try {
      await onSave(patch);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 2500);
    } catch (e) {
      setError(e.message || "Erreur enregistrement");
    } finally {
      setSaving(false);
    }
  }

  // L6 — champs titre/sous-titre de la langue ACTIVE uniquement.
  const curTitle = lang === "fr" ? titleFr : lang === "en" ? titleEn : titleEs;
  const setCurTitle =
    lang === "fr" ? setTitleFr : lang === "en" ? setTitleEn : setTitleEs;
  const curWelcome =
    lang === "fr" ? welcomeFr : lang === "en" ? welcomeEn : welcomeEs;
  const setCurWelcome =
    lang === "fr" ? setWelcomeFr : lang === "en" ? setWelcomeEn : setWelcomeEs;

  return (
    <div style={{ fontFamily: DS.fontBody, marginBottom: 14 }}>
      {/* L1a/L1b — 3 cartes repliables. Inversion de grille (L1b) : flex
          colonne + `order` → 📍 Localisation&météo + 🎯 GYG côte à côte en
          haut (order:1), ⚙️ Configuration pleine largeur dessous (order:2).
          Pur layout — blocs/logique/save/dirty inchangés. */}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 14,
          marginBottom: 14,
        }}
      >
        {/* ═════ ⚙️ Configuration : titre/sous-titre (FR/EN/ES) + slug ═════ */}
        <div style={{ order: 2 }}>
        <SectionCard
          {...LIVRET_CFG_CARD_META.config}
          subtitle="Sert à : titre + sous-titre du livret voyageur (langues) · slug de l'URL QR."
          collapsible
          open={openCards.config}
          onToggle={() => toggleCard("config")}
        >

      {/* L6 — n'affiche QUE la langue active (pilotée par le sélecteur global).
          Les langues vides sont traduites automatiquement depuis le FR AU SAVE
          (jamais d'écrasement d'une saisie/correction manuelle). */}
      <div style={{ marginBottom: 12 }}>
        <div style={lblStyle}>
          Titre {CFG_LANG_UP[lang]}
          {lang === "fr" ? " (obligatoire)" : " (optionnel)"}
        </div>
        <Inp
          value={curTitle}
          onChange={setCurTitle}
          placeholder={CFG_TITLE_PH[lang]}
        />
      </div>

      <div style={{ marginBottom: 12 }}>
        <div style={lblStyle}>Sous-titre {CFG_LANG_UP[lang]} (optionnel)</div>
        <Textarea
          value={curWelcome}
          onChange={setCurWelcome}
          placeholder={CFG_WELCOME_PH[lang]}
          rows={2}
        />
      </div>

      {lang !== "fr" && (
        <div
          style={{
            fontSize: 11,
            color: "#6b7a90",
            fontStyle: "italic",
            marginBottom: 14,
          }}
        >
          Laissé vide → traduit automatiquement depuis le FR à l'enregistrement.
        </div>
      )}

      {/* Slug (URL voyageur QR) — déplacé du bas vers la carte Configuration
          (L1a). Logique slug/rétrocompat old_slugs INCHANGÉE. */}
      {config?.slug && (
        <div style={{ marginBottom: 12 }}>
          <div style={lblStyle}>Slug (URL voyageur QR)</div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              fontFamily: "monospace",
              fontSize: 12,
            }}
          >
            <span style={{ color: "#64748b", whiteSpace: "nowrap" }}>
              /livret/qr/
            </span>
            <input
              type="text"
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
              placeholder="cocon-fbz"
              data-testid="livret-slug-input"
              style={{
                flex: 1,
                padding: "8px 12px",
                fontSize: 12,
                background: DS.white,
                border: `1px solid ${
                  slugChanged && !slugValid ? DS.red : DS.border
                }`,
                borderRadius: 7,
                color: DS.navy,
                fontFamily: "monospace",
                outline: "none",
              }}
            />
          </div>
          {slugChanged && !slugValid && (
            <div style={{ fontSize: 11, color: DS.red, marginTop: 4 }}>
              Slug invalide : minuscules, chiffres et tirets uniquement (3 à 60
              caractères).
            </div>
          )}
          {slugChanged && slugValid && (
            <div style={{ fontSize: 11, color: DS.warningText, marginTop: 4 }}>
              <AlertTriangle size={12} strokeWidth={1.8} style={{ verticalAlign: "-2px", marginRight: 4 }} />
              L'ancien slug «&nbsp;{initialSlug}&nbsp;» restera valide pour les
              QR déjà imprimés (redirection automatique).
            </div>
          )}
        </div>
      )}
        </SectionCard>
        </div>

        {/* 📍 Localisation & météo + 🎯 GYG côte à côte (row 1, order:1) */}
        <div
          style={{
            order: 1,
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 14,
            alignItems: "start",
          }}
        >
          {/* ═════ 📍 Localisation & météo ═════ */}
          <SectionCard
            {...LIVRET_CFG_CARD_META.loc}
            subtitle="Sert à : coordonnées (météo + carte) affichées dans le livret voyageur."
            collapsible
            open={openCards.loc}
            onToggle={() => toggleCard("loc")}
          >

      {/* Phase A.5 (ADR-117) — config météo (3 champs nullable). Priorité
          endpoint meteo_get : lat+lng (précis) > city (fallback) > null.
          PR-FIX-4d (ADR-162) — Carte Leaflet interactive : drag marqueur
          navy 🏠 met à jour weather_lat/weather_lng en local. Inputs lat/lng
          lecture seule par défaut (carte = source de vérité interactive),
          bouton "Saisir manuellement" pour passage édition texte fallback.
          ADR-176 — bouton 🔍 Géocoder + bandeau M-2 + textes aide. */}
      <details
        style={{
          marginBottom: 12,
          background: "#f8fafc",
          borderRadius: 8,
          padding: 8,
        }}
      >
        <summary
          style={{
            cursor: "pointer",
            fontSize: 12,
            fontWeight: 600,
            color: DS.navy,
            fontFamily: DS.fontBody,
          }}
        >
          <MapPin size={14} strokeWidth={1.8} style={{ verticalAlign: "-2px", marginRight: 6 }} />
          Coordonnées logement &amp; météo (optionnelle)
        </summary>

        {/* ADR-176 — bouton géocodage + textes d'aide M-1/M-5. Placé en haut
            de la section pour priorité visuelle. */}
        <div style={{ marginTop: 10, marginBottom: 12 }}>
          <button
            type="button"
            onClick={() => handleGeocode()}
            disabled={geocodeBtnDisabled}
            aria-label={geocodeBtnLabel}
            style={{
              padding: "10px 14px",
              minHeight: 44,
              background: geocodeBtnDisabled ? "transparent" : "transparent",
              color: geocodeBtnDisabled ? "#94a3b8" : DS.navy,
              border: `1px solid ${geocodeBtnDisabled ? "#cbd5e1" : DS.navy}`,
              borderRadius: 8,
              fontSize: 12,
              fontWeight: 600,
              cursor: geocodeBtnDisabled ? "not-allowed" : "pointer",
              fontFamily: DS.fontBody,
            }}
          >
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
              <Search size={14} strokeWidth={1.8} />
              {geocoding ? "Géocodage en cours…" : geocodeBtnLabel}
            </span>
          </button>

          {/* M-5 : texte d'aide inline si bouton disabled à cause adresse
              absente (pas de title HTML). */}
          {addressEmpty && (
            <div
              style={{
                fontSize: 11,
                color: "#64748b",
                marginTop: 4,
                fontFamily: DS.fontBody,
              }}
            >
              Renseignez d'abord l'adresse dans l'onglet Infos.
            </div>
          )}

          {/* M-1 : note permanente (toujours visible) save Infos. */}
          <div
            style={{
              fontSize: 11,
              color: "#64748b",
              marginTop: 4,
              fontFamily: DS.fontBody,
            }}
          >
            L'adresse géocodée est celle enregistrée en base. Sauvegardez
            l'onglet Infos d'abord si vous venez de la modifier.
          </div>
        </div>

        {/* M-2 : bandeau "Aucune coordonnée" si lat ET lng vides. */}
        {noCoordsYet && (
          <div
            data-testid="no-coords-banner"
            style={{
              display: "flex",
              alignItems: "flex-start",
              gap: 8,
              padding: "10px 14px",
              background: DS.warningBg,
              border: `0.5px solid #f1d9ac`,
              borderLeft: `3px solid ${DS.warning}`,
              borderRadius: 8,
              color: DS.warningText,
              fontSize: 12,
              fontFamily: DS.fontBody,
              marginBottom: 10,
            }}
          >
            <AlertTriangle size={14} strokeWidth={1.8} style={{ flex: "0 0 auto", marginTop: 1 }} />
            <span>Aucune coordonnée enregistrée pour ce logement. Cliquez
            « Géocoder » ou draggez le marqueur pour le positionner.</span>
          </div>
        )}

        {/* Zone erreur géocodage (rouge — distincte du save). Q-2 a11y :
            role=alert + aria-live=assertive (asymétrie avec geocode-info
            corrigée — erreur > info en urgence). */}
        {geocodeError && (
          <div
            data-testid="geocode-error"
            role="alert"
            aria-live="assertive"
            style={{
              display: "flex",
              alignItems: "center",
              gap: 9,
              padding: 10,
              background: DS.dangerBg,
              border: `0.5px solid ${DS.danger}`,
              borderRadius: 8,
              color: DS.dangerText,
              fontSize: 12,
              marginBottom: 10,
              fontFamily: DS.fontBody,
            }}
          >
            <AlertTriangle size={15} strokeWidth={1.8} style={{ flex: "0 0 auto" }} /> {geocodeError}
          </div>
        )}

        {/* Zone info positive (bleu doux — distincte erreur). Auto-hide 3s.
            Risque résiduel architect : message B-1 "Placement manuel
            conservé" est un INFO positif, pas une erreur. */}
        {geocodeInfo && (
          <div
            data-testid="geocode-info"
            role="status"
            aria-live="polite"
            style={{
              padding: 10,
              background: DS.cielSoft,
              border: `0.5px solid ${DS.ciel}`,
              borderRadius: 8,
              color: DS.marineDeep,
              fontSize: 12,
              marginBottom: 10,
              fontFamily: DS.fontBody,
            }}
          >
            {geocodeInfo}
          </div>
        )}

        {/* PR-FIX-4d (ADR-162) — Carte interactive marqueur draggable.
            ADR-176 M-2 — markerPlaceholder=true si pas de coords (opacity 0.5). */}
        <div style={{ marginTop: 10, marginBottom: 12 }}>
          <Suspense
            fallback={
              <div
                data-testid="map-loading-fallback"
                style={{
                  width: "100%",
                  height: 300,
                  background: "#f0f4f8",
                  borderRadius: 12,
                  border: `1px solid ${DS.border}`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#888",
                  fontSize: 12,
                  fontFamily: DS.fontBody,
                }}
              >
                Chargement de la carte…
              </div>
            }
          >
            <AlentoursMarkerDragMap
              centerLat={
                weatherLat.trim() && !Number.isNaN(Number(weatherLat))
                  ? Number(weatherLat)
                  : null
              }
              centerLng={
                weatherLng.trim() && !Number.isNaN(Number(weatherLng))
                  ? Number(weatherLng)
                  : null
              }
              onDragEnd={handleMarkerDragEnd}
              markerPlaceholder={noCoordsYet}
            />
          </Suspense>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "2fr 1fr 1fr",
            gap: 8,
            alignItems: "end",
          }}
        >
          <div>
            <div style={lblStyle}>Ville OWM (fallback)</div>
            <Inp
              value={weatherCity}
              onChange={setWeatherCity}
              placeholder="ex : Toulouse,FR"
            />
          </div>
          <div>
            <div style={lblStyle}>Latitude</div>
            <Inp
              value={weatherLat}
              onChange={manualLatLngEdit ? setWeatherLat : () => {}}
              placeholder="43.60426"
              disabled={!manualLatLngEdit}
            />
          </div>
          <div>
            <div style={lblStyle}>Longitude</div>
            <Inp
              value={weatherLng}
              onChange={manualLatLngEdit ? setWeatherLng : () => {}}
              placeholder="1.44367"
              disabled={!manualLatLngEdit}
            />
          </div>
        </div>

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginTop: 8,
            gap: 8,
            flexWrap: "wrap",
          }}
        >
          <div
            style={{
              fontSize: 11,
              color: "#64748b",
              fontFamily: DS.fontBody,
              flex: 1,
              minWidth: 200,
            }}
          >
            Priorité : lat+lng (précis) &gt; ville (fallback). Glissez le
            marqueur 🏠 ou saisissez manuellement.
          </div>
          <button
            type="button"
            onClick={() => setManualLatLngEdit((v) => !v)}
            aria-pressed={manualLatLngEdit}
            style={{
              padding: "10px 14px",
              minHeight: 44,
              background: manualLatLngEdit ? DS.navy : "transparent",
              color: manualLatLngEdit ? "#fff" : DS.navy,
              border: `1px solid ${DS.navy}`,
              borderRadius: 8,
              fontSize: 12,
              fontWeight: 600,
              cursor: "pointer",
              fontFamily: DS.fontBody,
            }}
          >
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
              <MapIcon size={14} strokeWidth={1.8} />
              {manualLatLngEdit ? "Utiliser la carte" : "Saisir manuellement"}
            </span>
          </button>
        </div>
      </details>
          </SectionCard>

          {/* ═════ 🎯 GetYourGuide ═════ */}
          <SectionCard
            {...LIVRET_CFG_CARD_META.gyg}
            subtitle="Sert à : widget activités GetYourGuide du livret voyageur."
            collapsible
            open={openCards.gyg}
            onToggle={() => toggleCard("gyg")}
          >

      {/* PR-A93-2b/1 (ADR-180) — GYG MVP livret voyageur (widget activités).
          Toggle d'activation + ID ville GetYourGuide (ex. 288 = Toulouse).
          Le partner_id est lu côté serveur depuis env var (jamais hardcodé). */}
      <details
        style={{
          marginBottom: 12,
          background: "#f8fafc",
          borderRadius: 8,
          padding: 8,
        }}
      >
        <summary
          style={{
            cursor: "pointer",
            fontSize: 12,
            fontWeight: 600,
            color: DS.navy,
            fontFamily: DS.fontBody,
          }}
        >
          <Ticket size={14} strokeWidth={1.8} style={{ verticalAlign: "-2px", marginRight: 6 }} />
          Activités GetYourGuide (optionnel)
        </summary>

        <div
          style={{
            fontSize: 12,
            color: "#475569",
            marginTop: 10,
            marginBottom: 10,
            lineHeight: 1.5,
            fontFamily: DS.fontBody,
          }}
        >
          Le widget affiche les activités touristiques GetYourGuide de la
          ville sélectionnée. Vous touchez une commission (8%) sur chaque
          réservation effectuée par un voyageur via votre livret.
        </div>

        {/* Toggle activation */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            marginBottom: 10,
          }}
        >
          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              cursor: "pointer",
              fontFamily: DS.fontBody,
              fontSize: 12,
              color: DS.navy,
              minHeight: 44,
            }}
          >
            <input
              type="checkbox"
              checked={gygEnabled}
              onChange={(e) => setGygEnabled(e.target.checked)}
              style={{ width: 18, height: 18, cursor: "pointer" }}
            />
            <span>
              Afficher le widget Activités dans le livret voyageur
            </span>
          </label>
        </div>

        {/* Input ID ville GYG */}
        <div style={{ marginBottom: 10 }}>
          <div style={lblStyle}>ID de la ville GetYourGuide</div>
          <Inp
            value={gygLocationId}
            onChange={(v) => {
              // Filtrage client : seulement chiffres, max 10 caractères.
              const cleaned = String(v).replace(/\D/g, "").slice(0, 10);
              setGygLocationId(cleaned);
            }}
            placeholder="288"
          />
          {gygLocationId && !gygIdValid && (
            <div
              style={{
                fontSize: 11,
                color: DS.red,
                marginTop: 4,
                fontFamily: DS.fontBody,
              }}
            >
              L'ID doit contenir 1 à 10 chiffres uniquement.
            </div>
          )}
          {gygActivationBlocked && (
            <div
              style={{
                fontSize: 11,
                color: DS.warningText,
                marginTop: 4,
                fontFamily: DS.fontBody,
              }}
            >
              Saisissez un ID de ville pour pouvoir activer le widget.
            </div>
          )}
        </div>

        {/* BUG 21 — Nombre d'activités affichées par le widget (par logement).
            Vide → défaut voyageur (5). Plage 1..50 (mirror serveur). */}
        <div style={{ marginBottom: 10 }}>
          <div style={lblStyle}>Nombre d'activités affichées (1 à 50)</div>
          <Inp
            value={gygNumberOfItems}
            onChange={(v) => {
              // Filtrage client : seulement chiffres, max 2 caractères.
              const cleaned = String(v).replace(/\D/g, "").slice(0, 2);
              setGygNumberOfItems(cleaned);
            }}
            placeholder="5 (défaut si laissé vide)"
          />
          {gygNumberOfItems && !gygItemsValid && (
            <div
              style={{
                fontSize: 11,
                color: DS.red,
                marginTop: 4,
                fontFamily: DS.fontBody,
              }}
            >
              Saisissez un nombre entre 1 et 50.
            </div>
          )}
          <div
            style={{
              fontSize: 11,
              color: "#64748b",
              marginTop: 4,
              fontFamily: DS.fontBody,
            }}
          >
            Laissez vide pour utiliser la valeur par défaut (5 activités).
          </div>
        </div>

        <div style={{ marginBottom: 10 }}>
          <a
            href="https://partner.getyourguide.com/"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 5,
              fontSize: 12,
              color: DS.marine,
              textDecoration: "underline",
              fontFamily: DS.fontBody,
            }}
          >
            <ExternalLink size={13} strokeWidth={1.8} /> Dashboard partenaire GetYourGuide
          </a>
        </div>

        <div
          style={{
            fontSize: 11,
            color: "#64748b",
            background: "#f0f4f9",
            padding: 10,
            borderRadius: 6,
            lineHeight: 1.5,
            fontFamily: DS.fontBody,
          }}
        >
          <strong>Astuce</strong> : ID Toulouse = <code>288</code>. Pour
          trouver l'ID d'une autre ville, allez sur{" "}
          <code>partner.getyourguide.com</code> &gt; Outils &gt; Widget
          ville &gt; recherchez la ville → l'ID apparaît dans le code généré
          (<code>data-gyg-location-id</code>).
        </div>
      </details>
          </SectionCard>
        </div>
      </div>
      {/* ═════ fin grille 3 cartes (L1a) ═════ */}

      {/* ADR-176 — Modal confirmation partial_match (inline, pas de
          composant modal externe). Overlay fixed → placement DOM indifférent. */}
      {pendingGeoResult && (
        <div
          data-testid="partial-match-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="geocode-partial-title"
          onClick={(e) => {
            if (e.target === e.currentTarget) setPendingGeoResult(null);
          }}
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0,0,0,0.45)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 2000,
            padding: 16,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="fbz-modal-sheet"
            style={{
              background: "#fff",
              borderRadius: 12,
              padding: 20,
              maxWidth: 480,
              width: "100%",
              boxShadow: "0 10px 30px rgba(0,0,0,.25)",
              fontFamily: DS.fontBody,
            }}
          >
            <div
              id="geocode-partial-title"
              style={{
                fontFamily: DS.fontDisplay,
                fontSize: 16,
                fontWeight: 700,
                color: DS.navy,
                marginBottom: 10,
              }}
            >
              Résultat approximatif — Confirmer ?
            </div>
            <div
              style={{
                fontSize: 13,
                color: "#374151",
                marginBottom: 8,
                lineHeight: 1.5,
              }}
            >
              Google a retourné un résultat approximatif pour cette adresse.
              Vérifiez que la position sur la carte correspond bien à votre
              logement.
            </div>
            <div
              style={{
                padding: 10,
                background: "#f8fafc",
                borderRadius: 6,
                fontSize: 13,
                fontWeight: 700,
                color: DS.navy,
                marginBottom: 14,
              }}
            >
              {pendingGeoResult.formatted_address}
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 8,
                flexWrap: "wrap",
              }}
            >
              <button
                type="button"
                onClick={() => setPendingGeoResult(null)}
                style={{
                  padding: "10px 14px",
                  minHeight: 44,
                  background: "transparent",
                  color: DS.navy,
                  border: `1px solid ${DS.navy}`,
                  borderRadius: 8,
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: "pointer",
                  fontFamily: DS.fontBody,
                }}
              >
                Annuler
              </button>
              <button
                type="button"
                autoFocus
                onClick={() => applyGeocoderResult(pendingGeoResult)}
                style={{
                  padding: "10px 14px",
                  minHeight: 44,
                  background: DS.skyDeep,
                  color: "#fff",
                  border: "none",
                  borderRadius: 8,
                  fontSize: 12,
                  fontWeight: 700,
                  cursor: "pointer",
                  fontFamily: DS.fontBody,
                }}
              >
                Confirmer et placer le marqueur
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ADR-176 B-3 — Modal Re-géocoder (fermeture dans finally). */}
      {showRegeocodeConfirm && (
        <div
          data-testid="regeocode-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="geocode-regeocode-title"
          onClick={(e) => {
            if (e.target === e.currentTarget && !geocoding) {
              setShowRegeocodeConfirm(false);
            }
          }}
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0,0,0,0.45)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 2000,
            padding: 16,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="fbz-modal-sheet"
            style={{
              background: "#fff",
              borderRadius: 12,
              padding: 20,
              maxWidth: 480,
              width: "100%",
              boxShadow: "0 10px 30px rgba(0,0,0,.25)",
              fontFamily: DS.fontBody,
            }}
          >
            <div
              id="geocode-regeocode-title"
              style={{
                fontFamily: DS.fontDisplay,
                fontSize: 16,
                fontWeight: 700,
                color: DS.navy,
                marginBottom: 10,
              }}
            >
              Re-géocoder ?
            </div>
            <div
              style={{
                fontSize: 13,
                color: "#374151",
                marginBottom: 14,
                lineHeight: 1.5,
              }}
            >
              Des coordonnées sont déjà renseignées (lat&nbsp;:&nbsp;
              <strong>{weatherLat || "—"}</strong> / lng&nbsp;:&nbsp;
              <strong>{weatherLng || "—"}</strong>). Le nouveau géocodage les
              écrasera. Vous pourrez affiner par drag ensuite.
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 8,
                flexWrap: "wrap",
              }}
            >
              <button
                type="button"
                onClick={() => setShowRegeocodeConfirm(false)}
                disabled={geocoding}
                style={{
                  padding: "10px 14px",
                  minHeight: 44,
                  background: "transparent",
                  color: geocoding ? "#94a3b8" : DS.navy,
                  border: `1px solid ${geocoding ? "#cbd5e1" : DS.navy}`,
                  borderRadius: 8,
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: geocoding ? "not-allowed" : "pointer",
                  fontFamily: DS.fontBody,
                }}
              >
                Annuler
              </button>
              <button
                type="button"
                autoFocus
                onClick={() =>
                  handleGeocode({ skipRegeocodeConfirm: true })
                }
                disabled={geocoding}
                style={{
                  padding: "10px 14px",
                  minHeight: 44,
                  background: geocoding ? "#cbd5e1" : DS.skyDeep,
                  color: "#fff",
                  border: "none",
                  borderRadius: 8,
                  fontSize: 12,
                  fontWeight: 700,
                  cursor: geocoding ? "not-allowed" : "pointer",
                  fontFamily: DS.fontBody,
                }}
              >
                {geocoding ? "Géocodage en cours…" : "Lancer le géocodage"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* (slug déplacé dans la carte ⚙️ Configuration — L1a) */}

      {error && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 9,
            padding: 10,
            background: DS.dangerBg,
            border: `0.5px solid ${DS.danger}`,
            borderRadius: 8,
            color: DS.dangerText,
            fontSize: 12,
            marginBottom: 12,
          }}
        >
          <AlertTriangle size={15} strokeWidth={1.8} style={{ flex: "0 0 auto" }} /> {error}
        </div>
      )}
      {success && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 9,
            padding: 10,
            background: DS.successBg,
            border: `0.5px solid ${DS.success}`,
            borderRadius: 8,
            color: DS.successText,
            fontSize: 12,
            marginBottom: 12,
          }}
        >
          <CheckCircle2 size={15} strokeWidth={1.8} style={{ flex: "0 0 auto" }} /> Config enregistrée
        </div>
      )}

      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <button
          type="button"
          onClick={handleSave}
          disabled={!canSave}
          className="fbz-press"
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 7,
            padding: "8px 18px",
            background: canSave ? DS.corail : "#cbd5e1",
            color: "#fff",
            border: "none",
            borderRadius: DS.radiusControl,
            fontWeight: 600,
            fontSize: 12,
            cursor: canSave ? "pointer" : "not-allowed",
            fontFamily: DS.fontBody,
          }}
        >
          <Save size={15} strokeWidth={1.8} /> {saving ? "Enregistrement…" : "Enregistrer config"}
        </button>
      </div>
    </div>
  );
}
